import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";

import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { organizationSchema } from "@/lib/structuredData";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

const categories = [
  "All Posts",
  "Raw Truth Talks",
  "Story-Driven Lessons",
  "Brotherhood Conversations"
];

export default function Blog() {
  const [selectedCategory, setSelectedCategory] = useState("All Posts");

  // Fetch published blog posts from database
  const { data: blogPosts = [], isLoading } = useQuery({
    queryKey: ['blog-posts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('status', 'published')
        .order('published_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
  });

  // Real-time subscription for new posts
  useEffect(() => {
    const channel = supabase
      .channel('blog-posts-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'blog_posts',
          filter: 'status=eq.published',
        },
        () => {
          // Refetch when posts change
          window.location.reload();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const filteredPosts = selectedCategory === "All Posts"
    ? blogPosts
    : blogPosts.filter(post => post.category === selectedCategory);

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Blog - Raw Truth, Real Stories"
        description="Long-form content for dads ready to stop performing and start living with soul. Read authentic stories about fatherhood, brotherhood, and awakening."
        keywords="fatherhood blog, dad blog, authentic masculinity blog, men's mental health, present parenting, brotherhood stories, raw truth talks"
        type="website"
        url="/blog"
        schema={organizationSchema}
      />
      <Navigation />
      <Breadcrumbs />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-hero relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-glow opacity-50" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-6 animate-fade-in">
            <h1 className="font-display text-5xl md:text-6xl font-bold text-primary-foreground">
              Raw Truth. Real Stories.<br />
              Real <span className="text-accent">Brotherhood</span>.
            </h1>
            <p className="text-xl text-primary-foreground/90 max-w-2xl mx-auto">
              Long-form content for dads ready to stop performing and start living with soul.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-muted/30 border-b border-border">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <Button
                key={category}
                variant={category === selectedCategory ? "default" : "outline"}
                className={category === selectedCategory ? "bg-accent hover:bg-accent/90" : ""}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          {isLoading ? (
            <div className="text-center py-20">
              <p className="text-muted-foreground">Loading posts...</p>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-20 max-w-2xl mx-auto">
              <h2 className="font-display text-3xl font-bold mb-4">No posts yet</h2>
              <p className="text-muted-foreground text-lg">
                Check back soon for raw truth, real stories, and authentic brotherhood.
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {filteredPosts.map((post, index) => (
              <Card 
                key={post.id}
                className="group hover:shadow-bold transition-all duration-300 overflow-hidden animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <Link to={`/blog/${post.slug}`}>
                  <div className="relative aspect-video bg-secondary overflow-hidden">
                    {post.image_url && (
                      <img 
                        src={post.image_url} 
                        alt={post.title}
                        width="1792"
                        height="1024"
                        loading={index < 3 ? "eager" : "lazy"}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-background/20 to-transparent" />
                    <Badge 
                      className="absolute top-4 left-4 bg-accent/90 text-accent-foreground z-10"
                    >
                      {post.category}
                    </Badge>
                  </div>
                  <CardContent className="p-6 space-y-4">
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(post.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                      </div>
                      <span>•</span>
                      <span>{post.read_time}</span>
                    </div>
                    
                    <h3 className="font-display text-xl font-bold text-foreground group-hover:text-accent transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    
                    <p className="text-muted-foreground line-clamp-3 leading-relaxed">
                      {post.excerpt}
                    </p>
                    
                    <div className="flex items-center text-accent font-medium group-hover:gap-3 transition-all">
                      Read More
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Link>
              </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
